import { resolveGroupName } from '../../EnrollQueue/queue-derive';
import { matchesQueueFilter } from './leadDisplay';
import { NoQueueMatches } from './NoQueueMatches';
import { PlaySection } from './PlaySection';
import type { QueueLandingTarget, QueuePaneViewProps } from './paneCommon';

export interface ByPlayProps extends QueuePaneViewProps {
  /** D4 — non-null while a landing is undismissed; matched per section by play type + instance ref. */
  landingTarget: QueueLandingTarget | null;
  /** Called by the section that actually scrolled, with the landing's nonce → `dismissQueuePlayLanding`. */
  onLandingScrolled: (nonce: number) => void;
}

/**
 * The Plays pane's By Play view (plan 2026-09-30-002, D1/D36, E65/E66) — one
 * `PlaySection` per assigned group, in server order.
 *
 * The D36 filter is applied HERE rather than inside `PlaySection`, because
 * what a filter hit means differs by what it hit (E65):
 *  - the PLAY NAME matches → the whole play stays, with every one of its
 *    leads visible. A rep who types a play name is asking for that batch, not
 *    for the subset of its leads whose own names happen to contain the same
 *    letters.
 *  - otherwise → only the leads that match themselves, and the section's
 *    `Send all N` / `Clear all N` counts follow those visible leads (that is
 *    why `PlaySection` counts `items`, the array passed in, and never
 *    `group.items`).
 *  - nothing in the section matches → the section is not rendered at all.
 *
 * A section is keyed `${play_type_id}:${play_instance_ref}` (never the array
 * index): two instances of the same play type are independent sections, and
 * the key is what keeps each one's local `open` state (D33) attached to its
 * own play as the filter adds and removes siblings around it.
 */
export function ByPlay({ groups, q, sendersFor, defaultSenderFor, section, landingTarget, onLandingScrolled }: ByPlayProps) {
  const sections = groups
    .map(group => {
      const playName = resolveGroupName(group);
      // `matchesQueueFilter(item, playName, q)` collapses to exactly this for
      // the name half; spelled out rather than called with an arbitrary item
      // so an empty group can still match by name.
      const nameMatches = q === '' || playName.toLowerCase().includes(q);
      const items = nameMatches ? group.items : group.items.filter(item => matchesQueueFilter(item, playName, q));
      return { group, items };
    })
    .filter(entry => entry.items.length > 0);

  if (sections.length === 0) {
    // E66 — "No matches" belongs to a NON-EMPTY filter only. With no filter
    // and no assigned groups there is simply nothing to render here: the pane
    // above may still be showing an `Unassigned · N` section, and claiming
    // "No matches" over it would be false.
    return q === '' ? null : <NoQueueMatches q={q} />;
  }

  return (
    // No gap: section headers share the flat rows' pitch (RailRow's RAIL_ROW_* constants).
    <div className="flex flex-col">
      {sections.map(({ group, items }, index) => (
        <PlaySection
          {...section}
          key={`${group.play_type_id}:${group.play_instance_ref}`}
          group={group}
          items={items}
          isFirst={index === 0}
          senders={sendersFor(group.play_type_id)}
          defaultSender={defaultSenderFor(group.play_type_id)}
          landingKey={
            landingTarget
              && landingTarget.playTypeId === group.play_type_id
              && landingTarget.playInstanceRef === group.play_instance_ref
              ? landingTarget.nonce
              : null
          }
          onLandingScrolled={onLandingScrolled}
        />
      ))}
    </div>
  );
}
